import { Autocomplete, Box, Button, Card, Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, Step, StepLabel, Stepper, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Formik } from "formik";
import { campaignApi } from "../../api/campaignApi";
import toast from 'react-hot-toast';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useEffect, useState, useCallback } from "react";
import * as React from 'react';
import { alpha } from '@mui/material/styles';
import { toneAlpha, getTone, lightVeil, grey } from '../../theme/tones';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const DialogReinjection = (propsData) => {
  const theme = useTheme();
  const { openReinjection, setOpenReinjection, initialValues, validationSchema, setIsLoading, idCampaign, typeCampaign, selectedStatus,
    right, donnees, selectedCommercialsReinjection, selectedCommercialReinjection, activeStep, steps, qualifications, setSelectedStatus,
    handleBack, handleNext, isLoading
  } = propsData;
  const [nbCustomersReinjected, setNbCustomersReinjected] = useState(0);
  const [loading, setLoading] = useState(false);

  const getNbCustomersReinjected = useCallback(async (values) => {
    const result = await campaignApi.getNbCustomersReinjected(idCampaign, typeCampaign, selectedStatus, right, donnees.commercial, selectedCommercialsReinjection, selectedCommercialReinjection, values, localStorage.getItem('accessToken'));
    return result;
  }, [idCampaign, typeCampaign, selectedStatus, right, donnees.commercial, selectedCommercialsReinjection, selectedCommercialReinjection]);

  useEffect(() => {
    const fetchNbCustomers = async () => {
      if (openReinjection) {
        try {
          setLoading(true);
          const result = await getNbCustomersReinjected(initialValues);
          if (result?.data) {
            setNbCustomersReinjected(typeof result.data === 'number' ? result.data : 0);
          }
        } catch (err) {
          console.error(err);
          setNbCustomersReinjected(0);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchNbCustomers();
  }, [openReinjection, initialValues, getNbCustomersReinjected]);

  return (
    <Dialog
      open={openReinjection}
      onClose={() => setOpenReinjection(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Paramètres de la réinjection</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              setIsLoading(true);
              await campaignApi.createReinjection(idCampaign, typeCampaign, selectedStatus, right, donnees.commercial, selectedCommercialsReinjection, selectedCommercialReinjection, values, localStorage.getItem('accessToken'));
              setStatus({ success: true });
              setSubmitting(false);
              toast.success('Réinjection créée avec succès');
              setOpenReinjection(false);
              setIsLoading(false);
              window.location.reload(false);
            } catch (err) {
              console.error(err);
              toast.error('Il y a eu un souci lors de la création de la réinjection !');
              setStatus({ success: false });
              setErrors({ submit: err.message });
              setSubmitting(false);
              setIsLoading(false);
            }
          }}
        >
          {({ errors, handleSubmit, isSubmitting, touched, values, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <Stepper
                activeStep={activeStep}
                sx={{
                  mb: 4,
                  mt: 2,
                  '& .MuiStepLabel-label': { fontWeight: 600 },
                  '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
                  '& .MuiStepIcon-root.Mui-completed': { color: 'success.main' }
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ minHeight: 180 }}>
                {activeStep === 0 && (
                  <Grid container alignItems="center" spacing={3}>
                    <Grid item xs={12} md={7}>
                      <Autocomplete
                        multiple
                        id="checkboxes-tags-demo"
                        options={qualifications || []}
                        value={selectedStatus}
                        disableCloseOnSelect
                        onChange={async (event, newValue) => {
                          setSelectedStatus([...newValue]);
                          setNbCustomersReinjected(await getNbCustomersReinjected(values).data);
                        }}
                        getOptionLabel={(option) => option.qualification}
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.qualification}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Statuts"
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              sx: { borderRadius: '12px' }
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                          <CircularProgress size={32} />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            backgroundColor: theme.palette.mode === 'light' ? getTone('blue').bg : toneAlpha('blue', 0.05),
                            borderRadius: '16px',
                            p: 2.5,
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1.5,
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'light' ? getTone('indigo').light : toneAlpha('blue', 0.15),
                          }}
                        >
                          <Typography
                            variant="h3"
                            color="primary"
                            sx={{ fontWeight: 800, color: 'secondary.dark' }}
                          >
                            {nbCustomersReinjected}
                          </Typography>
                          <Typography
                            color="text.secondary"
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                          >
                            prospects réinjectés
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                )}

                {activeStep === 1 && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: theme.palette.text.primary }}>
                      Récapitulatif de la réinjection
                    </Typography>
                    
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: '16px',
                        borderColor: theme.palette.divider,
                        backgroundColor: theme.palette.mode === 'light' ? grey(50) : lightVeil(0.01)
                      }}
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: theme.palette.text.secondary }}>
                            Statuts ciblés
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedStatus.length > 0 ? (
                              selectedStatus.map((status) => (
                                <Box
                                  key={status.qualification}
                                  sx={{
                                    py: 0.5,
                                    px: 1.5,
                                    borderRadius: '8px',
                                    backgroundColor: theme.palette.mode === 'light' ? grey(0) : lightVeil(0.05),
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    fontSize: '0.825rem',
                                    fontWeight: 600
                                  }}
                                >
                                  {status.qualification}
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Tous statuts
                              </Typography>
                            )}
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: theme.palette.text.secondary }}>
                            Assignation
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                            {values.assignment}
                          </Typography>

                          {values.assignment === 'Personnalisée' && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1 }}>
                                Commerciaux dont on réinjecte les prospects :
                              </Typography>
                              {selectedCommercialsReinjection.map((commercialId) => {
                                const comm = donnees.commercial.find(c => c.caller_id === parseInt(commercialId, 10));
                                return comm ? (
                                  <Typography
                                    key={commercialId}
                                    variant="body2"
                                    color="success.main"
                                    sx={{ fontWeight: 600, ml: 1, mt: 0.5 }}
                                  >
                                    • {comm.firstname} {comm.lastname}
                                  </Typography>
                                ) : null;
                              })}
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Card>

                    {errors.startDate && (
                      <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.startDate}</Typography>
                    )}
                    {errors.endDate && (
                      <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.endDate}</Typography>
                    )}
                    {errors.assignment && (
                      <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.assignment}</Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 3, pb: 1, gap: 1.5 }}>
                <Button
                  color="inherit"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, borderColor: 'divider' }}
                >
                  Précédent
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                {activeStep < 1 ? (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    sx={{
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                      color: grey(0),
                      borderRadius: '10px',
                      px: 3,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: (theme) => `0 4px 12px 0 ${alpha(theme.palette.secondary.main, 0.25)}`,
                      '&:hover': {
                        background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                      }
                    }}
                  >
                    {activeStep === steps.length - 2 ? 'Passer au récapitulatif' : 'Suivant'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.main} 100%)`,
                      color: grey(0),
                      borderRadius: '10px',
                      px: 3,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: (theme) => `0 4px 12px 0 ${alpha(theme.palette.success.main, 0.25)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${getTone('emerald').color} 0%, ${getTone('emerald').color} 100%)`
                      }
                    }}
                  >
                    Enregistrer
                  </Button>
                )}
                {isLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default DialogReinjection;
